import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnChanges, SimpleChanges } from '@angular/core';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { TranslateService } from '@ngx-translate/core';
import { SensorEvent } from 'src/app/models/sensorEvent';
import { SensorValue } from 'src/app/models/sensorValue';
import { SensorLocationType, SensorValueType } from 'src/app/models/constant/sensor';
import { LocationGridMapping } from 'src/app/models/locationGridMapping';
import { TenantSessionState } from 'src/app/store/state/tenant-session.state';
import { severityLabelKey } from '../severity';
import { PART_TYPE_KEYS, VehicleSensorLayout, buildLocationTypeMap, displayableSensors, isSensorStale, isTireLocationType, sensorIconFor, sensorSeverityCss, sensorSeverityValueCss, tireLocationsOf } from '../sensor-display';
import { isTrailerAssetType, tirePositionKey } from 'src/app/shared/pipes/tirePosition';

// A vehicle's sensors as tire grid + icon chips + sortable card list. Shared by
// the service-center drawer and the flag-gated vehicle detail summary.
@Component({
  selector: 'sc-sensor-panel',
  templateUrl: './sc-sensor-panel.component.html',
  styleUrls: ['./sc-sensor-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ScSensorPanelComponent implements OnChanges {
  @Input() sensors: SensorEvent[] = [];
  @Input() vehicleLayout: VehicleSensorLayout | null = null;
  // Withholds the icon group so tire sensors don't flash there before the grid claims them.
  @Input() layoutResolved = false;
  @Input() assetType: string | null = null;

  @SelectSnapshot(TenantSessionState.getLocationGridMapping) private locationGridMapping: LocationGridMapping;

  selectedSensorId: string | null = null;
  hoveredSensorId: string | null = null;
  sensorsSortBy: 'location' | 'severity' | 'date' = 'location';
  sensorsSortDesc = false;

  // Per-vehicle layout resolves the grid-key → SensorLocationType the sensor
  // event lacks. Null until it loads; while null all sensors fall to the icon group.
  private locationTypeMap: Map<string, SensorLocationType> | null = null;
  // Every tire position the layout knows about — the grid renders the ones
  // without a sensor as unpopulated tread so the wheel arrangement stays legible.
  tireLocations: string[] = [];
  // Fields, not getters — a fresh array identity every CD cycle would re-fire
  // the grid's ngOnChanges (and rebuild its tiles) on every hover.
  tireSensors: SensorEvent[] = [];
  iconSensors: SensorEvent[] = [];

  constructor(
    private cdr: ChangeDetectorRef,
    private el: ElementRef<HTMLElement>,
    private translate: TranslateService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.vehicleLayout) {
      this.locationTypeMap = buildLocationTypeMap(this.vehicleLayout);
      this.tireLocations = tireLocationsOf(this.locationTypeMap);
    }
    const displayable = this.displayableSensors;
    this.tireSensors = this.locationTypeMap ? displayable.filter(s => this.isTireSensor(s)) : [];
    this.iconSensors = this.locationTypeMap
      ? displayable.filter(s => !this.isTireSensor(s))
      : this.layoutResolved ? displayable : [];
  }

  get showSteeringWheel(): boolean {
    return !isTrailerAssetType(this.assetType);
  }

  // Sort-independent base — the grid tiles and icon chips read this so the
  // list sort never reshuffles them.
  private get displayableSensors(): SensorEvent[] {
    return displayableSensors(this.sensors);
  }

  get visibleSensors(): SensorEvent[] {
    const dir = this.sensorsSortDesc ? -1 : 1;
    return this.displayableSensors.sort((a, b) => dir * this.compareSensors(a, b));
  }

  sortSensors(col: 'location' | 'severity' | 'date'): void {
    if (this.sensorsSortBy === col) {
      this.sensorsSortDesc = !this.sensorsSortDesc;
    } else {
      this.sensorsSortBy = col;
      this.sensorsSortDesc = col !== 'location';
    }
    this.cdr.markForCheck();
  }

  private compareSensors(a: SensorEvent, b: SensorEvent): number {
    switch (this.sensorsSortBy) {
      case 'severity': {
        const sev = (s: SensorEvent) => (s.values?.length ? s.overallSeverityValue : -1);
        return sev(a) - sev(b);
      }
      case 'date': {
        const t = (s: SensorEvent) => new Date(s.dataLastReceived).getTime() || 0;
        return t(a) - t(b);
      }
      default: {
        const ka = this.locationSortKey(a);
        const kb = this.locationSortKey(b);
        return ka[0] - kb[0] || ka[1] - kb[1] || ka[2] - kb[2];
      }
    }
  }

  // Front-to-back down the canonical grid, tires leading; unmapped locations sink
  // to the end of their group.
  private locationSortKey(s: SensorEvent): [number, number, number] {
    const isTire = this.isTireSensor(s) || !!tirePositionKey(s.location, this.assetType);
    const pos = this.locationGridMapping?.mapping?.find(l => l.location === s.location)?.position;
    return [isTire ? 0 : 1, pos?.[0] ?? Number.MAX_SAFE_INTEGER, pos?.[1] ?? 0];
  }

  // sc-sensor-grid is tire-only. Without a resolved layout (or for a non-tire /
  // unmapped position) the sensor goes to the icon group — the grid is the
  // enhancement, the icon group the floor.
  private isTireSensor(sensor: SensorEvent): boolean {
    return isTireLocationType(this.locationTypeMap?.get(sensor.location));
  }

  isSensorStale = isSensorStale;

  sensorIcon(sensor: SensorEvent): string {
    return sensorIconFor(sensor, this.locationTypeMap?.get(sensor.location) ?? SensorLocationType.Null);
  }

  sensorLocationLabel(sensor: SensorEvent): string {
    const key = tirePositionKey(sensor.location, this.assetType);
    if (key) return this.translate.instant(key);
    if (sensor.description) return `${sensor.description} ${this.translate.instant('@sensor')}`;
    const label = this.sensorPartType(sensor)
      || this.locationGridMapping?.mapping?.find(l => l.location === sensor.location)?.description;
    return label || sensor.location || '';
  }

  // A description-named card repeats its type on the value row ("Engine Coolant
  // Temperature" twice) — collapse the row to the bare measure word ("Temperature").
  // Collapse on the raw English strings, translate last — a localized phrase may
  // not end with the measure word ("Voltaje de batería").
  sensorValueTypeLabel(sensor: SensorEvent, value: SensorValue): string {
    const raw = value.sensorValueType;
    const collapsed = this.sensorLocationLabel(sensor).includes(raw) ? raw.split(' ').pop() ?? raw : raw;
    return this.translate.instant(collapsed);
  }

  sensorPartType(sensor: SensorEvent): string {
    const type = this.locationTypeMap?.get(sensor.location);
    const key = type && PART_TYPE_KEYS[type];
    return key ? this.translate.instant(key) : '';
  }

  // Blank when the row label already IS the part type (non-tire rows).
  sensorPartTag(sensor: SensorEvent): string {
    const type = this.sensorPartType(sensor);
    return type && type !== this.sensorLocationLabel(sensor) ? type : '';
  }

  // A sensor with no evaluated (isOverall) reading still shows its raw readings —
  // the card must say what it measures, not sit empty.
  sensorValueRows(sensor: SensorEvent): SensorValue[] {
    const values = sensor.values ?? [];
    const overall = values.filter(v => v.isOverall === true);
    return overall.length ? overall : values;
  }

  isStaleReading(value: SensorValue): boolean {
    return value.isOverall && value.sensorValueType === SensorValueType.Staleness;
  }

  onSensorCardSelect(sensor: SensorEvent): void {
    this.selectedSensorId = this.selectedSensorId === sensor.sensorId ? null : sensor.sensorId;
    this.cdr.markForCheck();
  }

  onSensorTileSelect(sensor: SensorEvent): void {
    this.selectedSensorId = sensor.sensorId;
    this.cdr.markForCheck();
    setTimeout(() => this.el.nativeElement
      .querySelector(`.sensor-card[data-sensor-id="${sensor.sensorId}"]`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' }));
  }

  sensorSeverityKey(sensor: SensorEvent): string {
    if (!sensor.values?.length) return '@offline';
    if (sensor.overallSeverityValue < 0) return '@unevaluated';
    return severityLabelKey(sensor.overallSeverityValue);
  }

  sensorSeverityCssClass(sensor: SensorEvent): string {
    return sensorSeverityCss(sensor);
  }

  sensorValueCssClass(value: SensorValue): string {
    return sensorSeverityValueCss(value.severityValue);
  }

  sensorValueSeverityKey(value: SensorValue): string {
    return value.severityValue < 0 ? '@unevaluated' : severityLabelKey(value.severityValue);
  }
}
